import { useTheme, type ThemeMode } from '../context/ThemeContext.js';
import { Sun, Moon, Monitor, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../components/ui/cn.js';

const THEME_OPTIONS: { value: ThemeMode; label: string; desc: string; icon: React.ElementType; preview: string }[] = [
  {
    value:   'light',
    label:   'Light',
    desc:    'Always use light mode regardless of system setting.',
    icon:    Sun,
    preview: 'bg-white border-slate-200',
  },
  {
    value:   'dark',
    label:   'Dark',
    desc:    'Always use dark mode regardless of system setting.',
    icon:    Moon,
    preview: 'bg-slate-900 border-slate-700',
  },
  {
    value:   'system',
    label:   'Default',
    desc:    'Automatically follow your computer\'s system theme.',
    icon:    Monitor,
    preview: 'bg-gradient-to-br from-white to-slate-900 border-slate-300',
  },
];

export function ThemePage() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-8">
        <h1 className="text-xl font-bold text-slate-900">Theme</h1>
        <p className="text-sm text-slate-500 mt-0.5">Choose how the portal looks on your device.</p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {THEME_OPTIONS.map((t) => {
          const isActive = theme === t.value;
          const Icon = t.icon;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => { setTheme(t.value); toast.success(`Theme set to ${t.label}.`); }}
              className={cn(
                'flex items-center gap-5 px-5 py-4 rounded-xl border-2 text-left transition-all',
                isActive
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
              )}
            >
              {/* Preview swatch */}
              <div className={cn('w-12 h-12 rounded-lg border-2 shrink-0', t.preview)} />

              {/* Icon + text */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Icon className={cn('w-4 h-4', isActive ? 'text-blue-600' : 'text-slate-400')} />
                  <p className={cn('text-sm font-semibold', isActive ? 'text-blue-700' : 'text-slate-800')}>{t.label}</p>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
              </div>

              {/* Active indicator */}
              <div className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0',
                isActive ? 'border-blue-500 bg-blue-500' : 'border-slate-300',
              )}>
                {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-slate-400 mt-6">
        Your preference is saved locally and applied every time you open the portal.
      </p>
    </div>
  );
}
